import React from 'react';
import { ErrorBoundary } from '@rollbar/react';
import { Box, Typography, Button } from '@mui/material';

const ErrorFallback = ({ error, resetError }: { error: Error | null; resetError: () => void }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: 3,
        backgroundColor: 'var(--primary-colour)',
        color: 'var(--primary-text-colour)',
      }}
    >
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
        Oops! Something went wrong
      </Typography>
      <Typography variant="body1" sx={{ mb: 3, textAlign: 'center', maxWidth: 600 }}>
        We've been notified of the issue and will look into it. Please try refreshing the page.
      </Typography>
      {error && process.env.NODE_ENV === 'development' && (
        <Box
          sx={{
            mb: 3,
            p: 2,
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            borderRadius: 1,
            maxWidth: 800,
            overflow: 'auto',
          }}
        >
          <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
            {error.toString()}
          </Typography>
        </Box>
      )}
      <Button
        variant="contained"
        onClick={resetError}
        sx={{
          backgroundColor: 'var(--secondary-colour)',
          color: 'var(--primary-colour)',
          '&:hover': {
            backgroundColor: 'color-mix(in srgb, var(--secondary-colour) 85%, white)',
          },
        }}
      >
        Try Again
      </Button>
    </Box>
  );
};

export const RollbarErrorBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <ErrorBoundary fallbackUI={ErrorFallback}>
      {children}
    </ErrorBoundary>
  );
};
